import { LatLngTuple } from '@googlemaps/polyline-codec';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Dispatch } from 'redux';
import {
  AddAlert,
  Button,
  IconLocker,
  IconPencil,
  IconStar,
  IconTrashCan,
  IconWorld,
  IcoSearch,
  Spinner,
} from '../../../component';
import Switch from '../../../component/switch';
import { loaded, loading } from '../../../layout/redux/layoutActions';
import { generatePolygonsOnMap } from '../../../shared/utils/map';
import { clearDataLayers } from '../dataLayers/controllers/dataLayers.controller';
import { clearMap, modalOpen } from '../redux/dashboardActions';
import './polygons.scss';
import {
  displayOwnPolygons,
  refetchData,
  setNameAndIdToDelete,
  setNameAndIdToEdit,
  setSelectedSwitch,
} from './redux/polygonsActions';
import { editPolygons } from './redux/polygonsInfra';

export type Polygon = {
  id: string;
  title: string;
  createdAt: string;
  author: string;
  isFavorite: boolean;
  isPrivate: boolean;
  geometry: LatLngTuple[];
  radius: number;
};

interface IPolygonsProps {
  data: Polygon[];
  isVisible: boolean;
}

interface IEmptyStateProps {
  nls: { [key: string]: any };
}

interface IPolygonInfoProps {
  data: Polygon;
  lastElement: boolean;
  checked: boolean;
  dispatch: Dispatch<any>;
  nls: { [key: string]: any };
}

const EmptyState = ({ nls }: IEmptyStateProps): JSX.Element => {
  return (
    <>
      <div className="polygons-no-data-message">
        <p>{nls.noDataMessage}</p>
      </div>
      <div className="polygons-no-data-instructions">
        <p>{nls.steps.title}</p>
        <ol>
          <li>{nls.steps.first}</li>
          <li>{nls.steps.second}</li>
          <li>{nls.steps.third}</li>
          <li>{nls.steps.fourth}</li>
        </ol>
      </div>
    </>
  );
};

const NotFoundState = ({ nls }: IEmptyStateProps): JSX.Element => {
  return (
    <>
      <div className="polygons-not-found-message">
        <p>{nls.notFoundMessage}</p>
      </div>
    </>
  );
};

const PolygonInfo = ({
  data,
  lastElement,
  checked,
  dispatch,
  nls,
}: IPolygonInfoProps): JSX.Element => {
  const checking = (id: string): void => {
    if (checked) {
      dispatch(setSelectedSwitch(''));
    } else {
      dispatch(setSelectedSwitch(id));
    }
  };
  const makingFavorite = async (favorite: boolean): Promise<void> => {
    dispatch(loading());
    try {
      await editPolygons(data?.id, {
        polygon: {
          isFavorite: favorite,
        },
      });
      dispatch(
        AddAlert(
          'success',
          `"${data?.title}"${
            favorite ? nls.successAddFavorite : nls.successRemoveFavorite
          }`
        )
      );
    } catch {
      dispatch(
        AddAlert(
          'error',
          `${nls.failFavoriteFirstPart}"${data?.title}"${nls.failFavoriteSecondPart}`
        )
      );
    }
    dispatch([refetchData(), loaded()]);
  };

  const cleaningMap = (): void => {
    dispatch([clearDataLayers(), setSelectedSwitch(''), clearMap()]);
  };
  return (
    <div className="polygon-info-container">
      <div className="polygon-name-container">
        <div className="polygon-name-group-container">
          <Switch
            checked={checked}
            setChecked={() => {
              if (!checked) {
                cleaningMap();
                generatePolygonsOnMap(
                  data?.geometry,
                  '#000000',
                  '#383939',
                  data?.radius
                );
                checking(data?.id);
              } else {
                cleaningMap();
                checking('');
              }
            }}
            cy={`PolygonInfo${data?.id}`}
          />
          <p>{data?.title}</p>
        </div>
        <div className="polygon-actions-container">
          <Button
            className="btn"
            onClick={() => {
              makingFavorite(!data?.isFavorite);
            }}
            cy="PolygonFavoriteButton"
          >
            <IconStar cy="PolygonFavorite" isFavorited={data?.isFavorite} />
          </Button>
          <Button
            className="btn"
            onClick={() => {
              dispatch(setNameAndIdToEdit({ name: data?.title, id: data?.id }));
              dispatch(modalOpen('edit polygons'));
            }}
            cy="PolygonEditButton"
          >
            <IconPencil cy="PolygonEdit" />
          </Button>
          <Button
            className="btn"
            onClick={() => {
              dispatch(
                setNameAndIdToDelete({ name: data?.title, id: data?.id })
              );
              dispatch(modalOpen('delete polygons'));
            }}
            cy="PolygonDeleteButton"
          >
            <IconTrashCan cy="PolygonDelete" style={{ color: '#FFB8BB' }} />
          </Button>
        </div>
      </div>
      <div className="polygon-createdAt-container">
        {data?.isPrivate ? (
          <IconLocker cy="PolygonPermission" />
        ) : (
          <IconWorld cy="PolygonPermission" />
        )}
        <p>{`Criado por ${data?.author} em ${data?.createdAt}`}</p>
      </div>
      {lastElement ? <></> : <hr />}
    </div>
  );
};

export const Polygons = ({ data, isVisible }: IPolygonsProps): JSX.Element => {
  const [filteredData, setFilteredData] = useState<Polygon[]>([]);
  const { preference } = useSelector((state: any) => state.layoutState);
  const { idSelected } = useSelector(
    (state: any) => state.polygonsState.switches
  );
  const { isLoadingPolygons } = useSelector(
    (state: any) => state.polygonsState
  );
  const dispatch = useDispatch();
  const nls = require(`./nls/${preference.language.id}.json`);

  const filterData = (text: string): void => {
    const newData = data.filter((polygon) => {
      return polygon?.title.toLowerCase().includes(text.toLowerCase());
    });
    setFilteredData(newData);
  };
  useEffect(() => {
    setFilteredData(data);
  }, [data]);

  const componentToRender = (polygonData: Polygon[]): JSX.Element => {
    if (isLoadingPolygons) {
      return <Spinner />;
    }
    if (!filteredData.length && polygonData.length > 0) {
      return <NotFoundState nls={nls} />;
    }
    if (polygonData.length > 0) {
      return (
        <div className="polygons-container">
          {filteredData.map((polygon, index) => (
            <PolygonInfo
              data={polygon}
              key={polygon.title + index}
              lastElement={filteredData.length - 1 === index}
              checked={idSelected === polygon?.id}
              dispatch={dispatch}
              nls={nls}
            />
          ))}
        </div>
      );
    } else {
      return <EmptyState nls={nls} />;
    }
  };
  return (
    <>
      {isVisible && (
        <div className="polygons-store-container">
          <h1>{`${nls.title} (${data.length})`}</h1>
          <div className="box-search">
            <Button
              cy={'PolygonsSearchButton'}
              className={'btn'}
              onClick={() => {}}
            >
              <IcoSearch cy="PolygonsSearchIcon" />
            </Button>
            <div className="box-input-search active">
              <input
                type="text"
                id="polygonsSearch"
                placeholder={nls.placeholder}
                onChange={(e) => {
                  filterData(e.target.value);
                }}
              />
            </div>
          </div>
          <div className="polygons-checkbox-container">
            <div className="checkbox">
              <input
                data-cy="OwnPolygonsCheckbox"
                type="checkbox"
                onChange={(e) => {
                  dispatch(displayOwnPolygons(e.target.checked));
                }}
              />
              <span></span>
            </div>
            <p>{nls.checkboxLabel}</p>
          </div>
          <hr />
          {componentToRender(data)}
        </div>
      )}
    </>
  );
};
